import * as Sentry from '@sentry/browser';
import jwtDecode from 'jwt-decode';

import { SentryTag } from '../enums/SentryTag';
import DecodedToken from '../interfaces/DecodedToken';
import PayorderBackendErrorResponse from '../interfaces/PayorderBackendErrorResponse';
import { fetchAuthToken } from '../utils/auth';

function setSentryUserScope(token: string) {
  if (token) {
    const { sub, iss, retailUnit } = jwtDecode<DecodedToken>(token);
    Sentry.configureScope((scope) => {
      scope.setUser({ id: sub, issuer: iss, retailUnit });
    });
  }
}

type MakeRequestMethod = 'POST' | 'PUT' | 'GET' | 'DELETE';

interface IFetchClientConfig {
  includeCredentials?: boolean;
  extraHeaders?: NonNullable<unknown>;
}

const makeFetchClient = (clientId: string, config?: IFetchClientConfig) => {
  async function handleFetchError(response: Response) {
    if (!response.ok) {
      const error: PayorderBackendErrorResponse = await response.json();
      const message =
        error.error || error.errorMessage || `Unknown HTTP error with status ${response.status}.`;

      Sentry.captureEvent({ message, tags: { [SentryTag.ErrorType]: 'server' } });
      const info = { ...error, status: response.status };
      throw info;
    }

    return response;
  }

  async function makeRequest(url: string, method: MakeRequestMethod, payload?: any) {
    let token = '';
    let response: Response;

    try {
      token = await fetchAuthToken();
    } catch (error) {
      Sentry.captureException(error, (scope) => scope.setTag(SentryTag.ErrorType, 'auth'));
      throw error;
    }

    setSentryUserScope(token);

    try {
      response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Client-Id': clientId,
          ...(config?.extraHeaders ? { ...config.extraHeaders } : null),
        },
        body: payload ? JSON.stringify(payload) : undefined,
        ...(config?.includeCredentials ? { credentials: 'include' } : {}),
      });
    } catch (error) {
      // Capture and group errors that has to do with fetch, like CORS and
      // networks issues. This does not catch api failures like 500, 401 etc.
      Sentry.captureException(error, (scope) => {
        scope.setFingerprint(['fetch-errors']);
        return scope;
      });
      throw error;
    }

    return await handleFetchError(response);
  }

  return {
    post: async <T>(url: string, payload: any): Promise<T> => {
      const data = await makeRequest(url, 'POST', payload);
      return (await data.json()) as Promise<T>;
    },
    put: async <T>(url: string, payload: any): Promise<T | string> => {
      const data = await makeRequest(url, 'PUT', payload);
      return await data.text();
    },
    get: async <T>(url: string): Promise<T> => {
      const data = await makeRequest(url, 'GET');
      return (await data.json()) as Promise<T>;
    },
    delete: async <T>(url: string): Promise<T | string> => {
      const data = await makeRequest(url, 'DELETE');
      return await data.text();
    },
  };
};

export default makeFetchClient;
